import {
  Box,
  copyText,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  NotificationVariants,
  useGlobalToasts,
  useJsonModal,
  useMixpanel,
  VStack,
} from '@talos/kyoko';
import { useCallback, useState } from 'react';
import { useAppStateDispatch } from '../../../providers/AppStateProvider';
import { orderSliceActions, ResubmitSource } from '../../OMS/NewOrder/OrderSlice';
import { openView } from '../../OMS/OMSSlice';
import { OMSView } from '../../OMS/OMSView';
import type { PreTradeResponseData } from '../redux/preTradeApiTypes';
import { setPreTradeReference } from '../redux/preTradeSlice';
import { DetailsTab } from '../tabs/DetailsTab/DetailsTab';
import { HistoryTab } from '../tabs/HistoryTab/HistoryTab';
import { DEFAULT_HISTORY_FILTER, type HistoryFilter } from '../tabs/HistoryTab/useHistoryFilter';
import { ScenariosTab } from '../tabs/ScenariosTab/ScenariosTab';
import { useScenarioColors } from '../tabs/ScenariosTab/useScenarioColors';
import { useScenarioTabs } from '../tabs/ScenariosTab/useScenarioTabs';
import { PreTradeTabType, type PreTradeRouteState } from '../types';
import { mapPreTradeScenarioToResubmitOrder } from '../utils';
import { PreTradePanelHeader } from './PreTradePanelHeader/PreTradePanelHeader';

interface PreTradeProps {
  sessionId: string;
  scenarios: PreTradeResponseData[];
  isActiveSession: boolean;
  openTab: PreTradeRouteState['openTab'];
  requestId: string | undefined;
  onTabChange: (tab: PreTradeTabType, requestId?: string) => void;
}

export function PreTrade({ sessionId, scenarios, isActiveSession, openTab, requestId, onTabChange }: PreTradeProps) {
  const dispatch = useAppStateDispatch();
  const mixpanel = useMixpanel();
  const { add: addToast } = useGlobalToasts();
  const { activeTab, activeScenarioIndex, activeScenario, openScenarios, setActiveTab, showDetails, closeTab } =
    useScenarioTabs({ scenarios, sessionId, routeState: { openTab, requestId }, onTabChange });
  const colorMap = useScenarioColors(openScenarios);

  const { handleClickJson, jsonModal } = useJsonModal();
  const [showNonConsidered, setShowNonConsidered] = useState(false);
  const [historyFilter, setHistoryFilter] = useState<HistoryFilter>(DEFAULT_HISTORY_FILTER);

  const handleTabChange = useCallback(
    (tab: PreTradeTabType) => {
      setActiveTab(tab);
      onTabChange(tab);
    },
    [onTabChange, setActiveTab]
  );

  const handleScenarioSelect = useCallback(
    (index: number) => {
      mixpanel.track(MixpanelEvent.ChangePreTradeTabScenarioDetails, {
        [MixpanelEventProperty.Source]: MixpanelEventSource.PreTradeScenarioDetails,
      });
      showDetails(index);
      const scenario = scenarios[index];
      onTabChange(PreTradeTabType.Details, scenario?.RequestID);
    },
    [mixpanel, onTabChange, scenarios, showDetails]
  );

  const onUseScenario = useCallback(
    (index: number, source: MixpanelEventSource) => {
      const scenario = scenarios[index];
      if (!scenario) {
        return;
      }

      mixpanel.track(MixpanelEvent.UseScenario, { [MixpanelEventProperty.Source]: source });
      dispatch(openView(OMSView.NewOrderForm));
      const mappedOrder = mapPreTradeScenarioToResubmitOrder(scenario);
      dispatch(
        orderSliceActions.resubmitOrder({ order: mappedOrder, useRemaining: false, source: ResubmitSource.PreTrade })
      );
      // Track the primed scenario so OrderService can attach PreTradeRequestID if the form hasn't changed.
      // userSelected: true prevents subsequent API responses from overwriting this selection.
      dispatch(
        setPreTradeReference({
          sessionId,
          requestId: scenario.RequestID,
          nos: scenario.NewOrderSingle,
          userSelected: true,
        })
      );
      addToast({ text: 'Scenario applied to order form', variant: NotificationVariants.Positive });
    },
    [addToast, dispatch, mixpanel, scenarios, sessionId]
  );

  const onExportReport = useCallback(
    (source: MixpanelEventSource) => {
      mixpanel.track(MixpanelEvent.Export, { [MixpanelEventProperty.Source]: source });
      // TODO: Export pre-trade report for active scenario.
    },
    [mixpanel]
  );

  const onShowJson = useCallback(() => {
    if (activeScenario) {
      handleClickJson(activeScenario);
    }
  }, [activeScenario, handleClickJson]);

  const onCopyJson = useCallback(() => {
    if (!activeScenario) {
      return;
    }

    mixpanel.track(MixpanelEvent.CopyJSON);
    copyText(JSON.stringify(activeScenario, null, 2));
  }, [activeScenario, mixpanel]);

  return (
    <>
      <VStack h="100%" alignItems="stretch" justifyContent="flex-start" gap="spacing0" overflow="hidden">
        <PreTradePanelHeader
          scenarios={scenarios}
          activeTab={activeTab}
          activeScenarioIndex={activeScenarioIndex}
          openScenarios={openScenarios}
          isActiveSession={isActiveSession}
          onTabChange={handleTabChange}
          onScenarioSelect={handleScenarioSelect}
          onTabClose={closeTab}
          onUseScenario={onUseScenario}
          onExportReport={onExportReport}
          onShowJson={onShowJson}
          onCopyJson={onCopyJson}
          showNonConsidered={showNonConsidered}
          onToggleShowNonConsidered={() => setShowNonConsidered(prev => !prev)}
        />

        <Box flex={1} overflow="hidden" overflowY="auto">
          {activeTab === PreTradeTabType.History && (
            <HistoryTab
              sessionId={sessionId}
              switchToScenarios={() => handleTabChange(PreTradeTabType.Scenarios)}
              historyFilter={historyFilter}
              onHistoryFilterChange={setHistoryFilter}
            />
          )}

          {activeTab === PreTradeTabType.Scenarios && (
            <ScenariosTab
              scenarios={openScenarios}
              activeScenarioIndex={activeScenarioIndex}
              colorMap={colorMap}
              onShowDetails={handleScenarioSelect}
              onUseScenario={onUseScenario}
            />
          )}

          {activeTab === PreTradeTabType.Details && activeScenario && (
            <DetailsTab
              scenario={activeScenario}
              showNonConsidered={showNonConsidered}
              isActiveSession={isActiveSession}
            />
          )}
        </Box>
      </VStack>
      {jsonModal}
    </>
  );
}
